import { useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import Actionbar from 'src/components/common/actionbar/Authactionbar';
import { ImagePreviewModal } from 'src/components/common/form/ImagePreviewModal';
import { Message } from 'src/components/common/form/Message';
import useUiuxStore from 'src/store/useUiuxStore';
import { useMouseContent } from 'src/utils/mouse/mouseContent';
import { HRM1030_R01Content } from './Content_HRM1030_R01';
import { HRM1030_R01FileDto, HRM1030_R01HeaderGridRow, HRM1030_R01TrainingDto } from './Dto_HRM1030_R01';
import { FileTable_HRM1030_R01 } from './FileTable_HRM1030_R01';
import { HRM1030_R01SearchSection } from './SearchSection_HRM1030_R01';
import { Table_HRM1030_R01 } from './Table_HRM1030_R01';
import { TrainingTable_HRM1030_R01 } from './TrainingTable_HRM1030_R01';

/** 사원 목록 왼쪽 너비(%)의 저장 키와 최소·최대·기본값이다(HRM1040과 같은 형태). */
const WIDTH_RESIZER = {
    code: 'HRM1030_R01',
    type: 'width',
    storageKey: 'HRM1030_R01-split-width',
    min: 25,
    max: 65,
    default: 36,
} as const;
/** 교육 이력 영역 위쪽 높이(%)의 저장 키와 최소·최대·기본값이다. */
const HEIGHT_RESIZER = {
    code: 'HRM1030_R01',
    type: 'height',
    storageKey: 'HRM1030_R01-split-height',
    min: 30,
    max: 80,
    default: 58,
} as const;

/**
 * 교육훈련 등록 화면을 액션바, 검색, 사원 목록, 교육 이력·수료증 첨부 그리드, 이미지 확대 팝업으로 조립한다.
 * @returns 사원 목록(좌)과 교육 이력·수료증 첨부(우, 위아래)로 나뉜 화면 JSX
 */
export const Container_HRM1030_R01 = () => {
    // 저장 전 편집을 확정할 세 그리드의 참조
    const gridRef = useRef<AgGridReact<HRM1030_R01HeaderGridRow>>(null);
    const trainingGridRef = useRef<AgGridReact<HRM1030_R01TrainingDto>>(null);
    const fileGridRef = useRef<AgGridReact<HRM1030_R01FileDto>>(null);

    // 좌우 및 상하 분할 영역의 참조와 드래그 상태
    const widthContainerRef = useRef<HTMLDivElement>(null);
    const widthInfoRef = useRef<HTMLDivElement>(null);
    const widthResizing = useRef(false);
    const heightContainerRef = useRef<HTMLDivElement>(null);
    const heightInfoRef = useRef<HTMLDivElement>(null);
    const heightResizing = useRef(false);

    // 좌우 분할 비율과 경계선 드래그 시작 함수
    const { splitValue: width, handleMouseDown: startWidth } = useMouseContent({
        resizer: WIDTH_RESIZER,
        isResizing: widthResizing,
        containerRef: widthContainerRef,
        infoRef: widthInfoRef,
    });
    // 상하 분할 비율과 경계선 드래그 시작 함수
    const { splitValue: height, handleMouseDown: startHeight } = useMouseContent({
        resizer: HEIGHT_RESIZER,
        isResizing: heightResizing,
        containerRef: heightContainerRef,
        infoRef: heightInfoRef,
    });

    // 하단 메시지 변경 함수를 가져온다.
    const { setMessage } = useUiuxStore();
    // 화면 동작 함수와 이미지 팝업 상태
    const {
        fetchData,
        handleHeaderRowClick,
        handleTrainingSelect,
        handleFilesAdd,
        handleFileOpen,
        handleFileDownload,
        handleDeleteClick,
        handleSaveClick,
        handleExcelClick,
        initContainerData,
        imagePreview,
        closeImagePreview,
    } = HRM1030_R01Content({ gridRef, trainingGridRef, fileGridRef, setMessage });
    return (
        <div className="content-container">
            {/* 공통 액션바. 교육 행 추가는 그리드의 +로 하므로 등록은 연결하지 않고 메뉴 권한으로 끈다(HRM1040과 같음). */}
            <Actionbar onFetchClick={fetchData} onDeleteClick={handleDeleteClick}
                onSaveClick={handleSaveClick} onExcelClick={handleExcelClick} onInitClick={initContainerData} />
            {/* 교육년도·교육구분·사원 검색 조건 */}
            <HRM1030_R01SearchSection />
            <div className="flex flex-1 min-h-0 p-[12px] gap-[4px]" ref={widthContainerRef}>
                <div className="min-w-0 min-h-0" style={{ width: `${width}%` }}>
                    {/* 조회 전용 사원 목록. 행 클릭 시 선택 사원을 바꾼다. */}
                    <Table_HRM1030_R01 gridRef={gridRef} onSelect={handleHeaderRowClick} />
                </div>
                {/* 좌우 크기 조절 경계선 */}
                <div className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
                    onMouseDown={event => startWidth('LR', event.nativeEvent)} />
                <div className="flex flex-col min-w-0 min-h-0 gap-[4px]" style={{ width: `${100 - width}%` }}
                    ref={widthInfoRef}>
                    <div className="flex flex-col flex-1 min-h-0 gap-[4px]" ref={heightContainerRef}>
                        <div className="min-h-0" style={{ height: `${height}%` }}>
                            {/* 선택 사원의 교육훈련 이력 편집 그리드와 요약 줄. 행 클릭 시 첨부 대상 교육을 바꾼다. */}
                            <TrainingTable_HRM1030_R01 gridRef={trainingGridRef}
                                onTrainingSelect={row => void handleTrainingSelect(row)} />
                        </div>
                        {/* 위아래 크기 조절 경계선 */}
                        <div className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300"
                            onMouseDown={event => startHeight('UD', event.nativeEvent)} />
                        <div className="min-h-0" style={{ height: `${100 - height}%` }} ref={heightInfoRef}>
                            {/* 선택 교육의 수료증 첨부 그리드. 끌어놓기·파일 선택·열람·다운로드를 연결한다. */}
                            <FileTable_HRM1030_R01 gridRef={fileGridRef}
                                onFilesAdd={files => void handleFilesAdd(files)}
                                onFileOpen={row => void handleFileOpen(row)}
                                onFileDownload={row => void handleFileDownload(row)} />
                        </div>
                    </div>
                </div>
            </div>
            {/* 이미지 수료증 확대 팝업(공통). ?.·??: 열 파일이 없으면 빈 값으로 닫힌 상태를 유지한다. */}
            <ImagePreviewModal
                isOpen={!!imagePreview}
                title={imagePreview?.title ?? ''}
                image={imagePreview?.image ?? ''}
                onClose={closeImagePreview} />
            {/* 하단 결과 메시지 */}
            <Message />
        </div>
    );
};
